import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { keys, branchKey, tenantKey } from '@/lib/queryKeys';
import {
  searchCustomers, registerCustomer, fetchCustomer, fetchRepeatMedicines, fetchCustomerPurchases,
  captureOutpatientPrescription, createOutpatientSale, fetchOutpatientSales, fetchOutpatientSale,
  createPharmacyReturn, fetchPharmacyReturns, settlePharmacyRefund,
  fetchLoyaltySettings, fetchLoyaltyBalance, fetchLoyaltyHistory, quoteLoyalty,
} from './api';
import type { CreateOutpatientSaleInput, CreateReturnInput, OutpatientPrescriptionInput, PaymentMode } from './types';

/**
 * Data hooks for the walk-in pharmacy counter and the loyalty programme.
 *
 * The scoping split here is the one HMS_V4_API_CONTRACT.md §5 exists to
 * enforce, and it is genuinely mixed on this screen — which is exactly the
 * situation that used to produce silent cache bugs:
 *
 *   tenant-wide   the customer, their loyalty balance and history. A patient
 *                 is a patient at every branch, and points follow the person.
 *   branch-scoped the sales, the returns, and the repeat-medicine list — all
 *                 of which resolve against ONE branch's physical stock and
 *                 till, so every one of them appends branchVersion.
 */

// ── Customers ────────────────────────────────────────────────────

/** Debouncing is the caller's business; this only refuses to fire on a blank box. */
export function useCustomerSearch(query: string) {
  return useQuery({
    queryKey: keys.counter.customerSearch(query),
    queryFn: () => searchCustomers(query),
    enabled: query.trim().length >= 2,
  });
}

export function useCustomer(patientId: number | null) {
  return useQuery({
    queryKey: keys.counter.customer(patientId ?? 0),
    queryFn: () => fetchCustomer(patientId as number),
    enabled: patientId != null,
  });
}

export function useRegisterCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: registerCustomer,
    // A new patient changes who the front desk's own pickers can find, not
    // just this screen's — invalidate the shared patient roster too.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.patients.all() });
      queryClient.invalidateQueries({ queryKey: tenantKey('counter', 'customers'), exact: false });
    },
  });
}

/**
 * The repeat-purchase list — the reason a counter sale is linked to a real
 * patient record at all. Branch-scoped because each past purchase comes back
 * resolved to the batch that would be sold today at THIS branch.
 */
export function useRepeatMedicines(patientId: number | null) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.pharmacy.repeatMedicines(patientId ?? 0), branchVersion],
    queryFn: () => fetchRepeatMedicines(patientId as number),
    enabled: patientId != null,
  });
}

export function useCustomerPurchases(patientId: number | null) {
  return useQuery({
    queryKey: keys.counter.purchases(patientId ?? 0),
    queryFn: () => fetchCustomerPurchases(patientId as number),
    enabled: patientId != null,
  });
}

// ── Prescription capture ─────────────────────────────────────────

export function useCaptureOutpatientPrescription() {
  return useMutation({
    mutationFn: (input: OutpatientPrescriptionInput) => captureOutpatientPrescription(input),
  });
}

// ── The sale ─────────────────────────────────────────────────────

/**
 * Invalidates everything one sale actually moves: stock (quantity went down),
 * the day's sales list, the customer's own purchase history and repeat list,
 * and their points. The loyalty keys are invalidated unconditionally rather
 * than only when points were involved — a sale that earned zero points still
 * moved `last_activity_at`, and a stale balance on a screen where the next
 * customer is about to redeem is not a harmless cache miss.
 */
export function useCreateOutpatientSale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOutpatientSaleInput) => createOutpatientSale(input),
    onSuccess: (_sale, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.pharmacy.inventory() });
      queryClient.invalidateQueries({ queryKey: branchKey('pharmacy', 'sales'), exact: false });
      queryClient.invalidateQueries({ queryKey: keys.pharmacy.repeatMedicines(variables.patient_id) });
      queryClient.invalidateQueries({ queryKey: keys.counter.customer(variables.patient_id) });
      queryClient.invalidateQueries({ queryKey: keys.counter.purchases(variables.patient_id) });
      queryClient.invalidateQueries({ queryKey: keys.loyalty.balance(variables.patient_id) });
      queryClient.invalidateQueries({ queryKey: keys.loyalty.history(variables.patient_id) });
      // The patient's Billing tab in Patient Records lists this bill too — a
      // counter sale is an ordinary bill on an ordinary patient record, which
      // is the whole point of not having built a separate sales table.
      queryClient.invalidateQueries({ queryKey: keys.clinical.billsForPatient(variables.patient_id) });
    },
  });
}

export function useOutpatientSales(params?: { date?: string; channel?: 'all' | 'outpatient' | 'admission' }) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.pharmacy.sales(params as Record<string, unknown>), branchVersion],
    queryFn: () => fetchOutpatientSales(params),
  });
}

export function useOutpatientSale(id: number | null) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.pharmacy.sale(id ?? 0), branchVersion],
    queryFn: () => fetchOutpatientSale(id as number),
    enabled: id != null,
  });
}

// ── Returns ──────────────────────────────────────────────────────

/**
 * A return moves stock back (sometimes), refunds money, and unwinds points —
 * so it invalidates the same set a sale does, plus the returns list and the
 * one sale whose status just changed to partly-returned/cancelled.
 */
export function useCreatePharmacyReturn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ saleId, input }: { saleId: number; input: CreateReturnInput }) => createPharmacyReturn(saleId, input),
    onSuccess: (ret, { saleId }) => {
      queryClient.invalidateQueries({ queryKey: keys.pharmacy.inventory() });
      queryClient.invalidateQueries({ queryKey: keys.pharmacy.sale(saleId) });
      queryClient.invalidateQueries({ queryKey: branchKey('pharmacy', 'sales'), exact: false });
      queryClient.invalidateQueries({ queryKey: branchKey('pharmacy', 'returns'), exact: false });
      queryClient.invalidateQueries({ queryKey: keys.pharmacy.stockAdjustments() });
      if (ret?.patient_id) {
        queryClient.invalidateQueries({ queryKey: keys.loyalty.balance(ret.patient_id) });
        queryClient.invalidateQueries({ queryKey: keys.loyalty.history(ret.patient_id) });
        queryClient.invalidateQueries({ queryKey: keys.counter.purchases(ret.patient_id) });
      }
    },
  });
}

export function usePharmacyReturns(params?: { date?: string; status?: 'Paid' | 'Pending' }) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.pharmacy.returns(params?.date), params?.status ?? null, branchVersion],
    queryFn: () => fetchPharmacyReturns(params),
  });
}

/**
 * Settles a pending refund. Touches only the money: the stock and the points
 * moved when the return was recorded, so neither inventory nor the loyalty
 * ledger is invalidated here — just the return lists and the one sale whose
 * detail shows its returns.
 */
export function useSettlePharmacyRefund() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ returnId, refundMode }: { returnId: number; refundMode?: PaymentMode }) =>
      settlePharmacyRefund(returnId, refundMode),
    onSuccess: (ret) => {
      queryClient.invalidateQueries({ queryKey: branchKey('pharmacy', 'returns'), exact: false });
      if (ret?.bill_id) queryClient.invalidateQueries({ queryKey: keys.pharmacy.sale(ret.bill_id) });
    },
  });
}

// ── Loyalty ──────────────────────────────────────────────────────

/**
 * Tenant-wide config, read by both the counter (to decide whether to show a
 * points panel at all) and the admin settings tab. staleTime is generous
 * because these rules change roughly never, and the counter re-reads them on
 * every customer otherwise.
 */
export function useLoyaltySettings() {
  return useQuery({
    queryKey: keys.loyalty.settings(),
    queryFn: fetchLoyaltySettings,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLoyaltyBalance(patientId: number | null, enabled = true) {
  return useQuery({
    queryKey: keys.loyalty.balance(patientId ?? 0),
    queryFn: () => fetchLoyaltyBalance(patientId as number),
    enabled: patientId != null && enabled,
  });
}

export function useLoyaltyHistory(patientId: number | null) {
  return useQuery({
    queryKey: keys.loyalty.history(patientId ?? 0),
    queryFn: () => fetchLoyaltyHistory(patientId as number),
    enabled: patientId != null,
  });
}

/**
 * Live redemption cap for the cart as it stands. Deliberately NOT cached
 * across amounts (the key carries the amount) and never trusted as final —
 * the sale endpoint re-quotes server-side before spending anything, because
 * this answer goes stale the moment another till touches the same balance.
 */
export function useLoyaltyQuote(patientId: number | null, amount: number, enabled = true) {
  return useQuery({
    queryKey: [...keys.loyalty.balance(patientId ?? 0), 'quote', Math.round(amount * 100)],
    queryFn: () => quoteLoyalty(patientId as number, amount),
    enabled: patientId != null && enabled && amount > 0,
  });
}
